import { Calendar, MapPin, Clock, ChevronRight } from 'lucide-react'
import { useLocation } from 'wouter'
import { getImageUrls } from '@/utils/image-urls'

export interface Event {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  date_start?: string
  date_end?: string
  event_dates?: string
  location?: string
  price_text?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
}

interface EventCardProps {
  event: Event
}

export default function EventCard({ event }: EventCardProps) {
  const [, navigate] = useLocation()

  const handleClick = () => {
    navigate(`/events/${event.id}`)
  }

  const imageUrls = event.filename ? getImageUrls('events', event.filename) : null

  const formattedDate = event.date_start
    ? new Date(event.date_start).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      })
    : event.event_dates

  return (
    <div
      onClick={handleClick}
      className="bg-[#3C3C3C] rounded-xl overflow-hidden hover:bg-[#4C4C4C] transition-colors cursor-pointer"
    >
      {/* Event Image */}
      {imageUrls && (
        <div className="w-full overflow-hidden">
          <img
            src={imageUrls.poster}
            alt={event.filename_alt || event.heading}
            className="w-full h-auto"
          />
        </div>
      )}

      {/* Event Content */}
      <div className="p-4">
        {/* Price Badge */}
        {event.price_text && (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-[#EF4444]/20 text-[#EF4444] rounded-md mb-2">
            {event.price_text}
          </span>
        )}

        {/* Title */}
        <h3 className="text-[#ffffff] font-semibold text-lg mb-2 line-clamp-2">
          {event.heading}
        </h3>

        {/* Description */}
        {event.page_description && (
          <p className="text-[#BABABB] text-sm mb-3 line-clamp-2">
            {event.page_description}
          </p>
        )}

        {/* Event Details */}
        <div className="space-y-2">
          {formattedDate && (
            <div className="flex items-center gap-2 text-[#BABABB] text-sm">
              <Calendar className="h-4 w-4 text-[#EF4444]" />
              <span>{formattedDate}</span>
            </div>
          )}

          {event.location && (
            <div className="flex items-center gap-2 text-[#BABABB] text-sm">
              <MapPin className="h-4 w-4 text-[#EF4444]" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          )}
        </div>

        {/* View Details Arrow */}
        <div className="flex items-center justify-end mt-3 text-[#EF4444]">
          <span className="text-sm font-medium">View Details</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </div>
  )
}
